import { useLogOutMutation } from "@/data/queries"
import { ROUTES } from "@/routes"
import cn from "@/utils/cn"
import { ComponentPropsWithoutRef, ReactNode } from "react"
import { useNavigate } from "react-router"

type SidebarProps = {
  isExpanded: boolean
  onToggle: () => void
  children: ReactNode
} & ComponentPropsWithoutRef<"aside">

type SidebarItemProps = {
  icon: string
  label: string
  active: boolean
  onClick: () => void
  isExpanded: boolean
}

const Sidebar = ({
  isExpanded,
  onToggle,
  children,
  className,
  ...otherProps
}: SidebarProps) => {
  const navigate = useNavigate()
  const { mutate: logout } = useLogOutMutation()

  const handleLogout = () => {
    logout(undefined)
    navigate(ROUTES.LOGIN)
  }

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-30 flex h-full flex-col bg-primary text-white transition-all duration-300",
        isExpanded ? "w-60" : "w-12",
        className
      )}
      {...otherProps}
    >
      <div
        className={cn(
          "flex h-12 items-center justify-center border-b border-white/20 px-4",
          isExpanded && "justify-end"
        )}
      >
        <button
          onClick={onToggle}
          className="flex items-center justify-center rounded transition-colors hover:bg-white/10"
          aria-label={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
        >
          <span className="material-icons text-icon-lg">
            {isExpanded ? "chevron_left" : "chevron_right"}
          </span>
        </button>
      </div>

      <nav className="flex-grow overflow-y-auto overflow-x-hidden">
        {children}
      </nav>

      <div
        className={cn(
          "flex h-12 items-center justify-center border-t border-white/20 px-4",
          isExpanded && "justify-end"
        )}
      >
        <button
          onClick={handleLogout}
          className="flex items-center justify-center gap-3 rounded p-0.5 transition-colors hover:bg-white/10"
          aria-label="Sign out"
        >
          <span className="material-icons !text-icon-lg">logout</span>
          {isExpanded && (
            <span className="whitespace-nowrap text-sm">Sign Out</span>
          )}
        </button>
      </div>
    </aside>
  )
}

const SidebarItem = ({
  icon,
  label,
  active,
  onClick,
  isExpanded,
}: SidebarItemProps) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex h-12 w-full items-center gap-3 px-4 py-3 transition-colors",
        active ? "bg-white/20 hover:bg-white/25" : "hover:bg-white/10",
        !isExpanded && "justify-center px-0"
      )}
    >
      <span className="material-icons !text-icon-lg">{icon}</span>
      {isExpanded && (
        <span className="whitespace-nowrap text-sm font-medium">{label}</span>
      )}
    </button>
  )
}

Sidebar.Item = SidebarItem

export default Sidebar
